import React, { useState, useEffect } from 'react';
import apiDefinitions from '../../../../core/api/apiDefinitions';
import StepOne from './StepOne';
import StepTwo from './StepTwo';
import StepThree from './StepThree';
import StepFour from './stepfour'; // Fixed typo in import
import StepFive from './StepFive';
import {
  showSuccessAlert,
  showError,
  showConfirmSubmission,
} from '../../../../core/alert/sweetAlertConfig';

// Define interfaces for type safety
interface VehicleType {
  id: number;
  type: string;
}

interface VehicleFeature {
  id: number;
  featureName: string;
}

interface FuelType {
  id: number;
  fuelTypeName: string;
}

interface BrandData {
  data: {
    vehicleBrands: { id: number; name: string; models: { id: number; name: string }[] }[];
  };
}

interface FormErrors {
  [key: string]: string;
}

interface Config {
  conditions: string[];
  states: string[];
  gearTypes: string[];
  fuelTypes: string[];
  vehicleClasses: string[];
  emissionOverallStatus: string[];
  revenueLicenceStatus: string[];
  features: { [key: string]: string[] };
  brandModels: { [key: string]: string[] };
}

interface EmissionTestData {
  numberPlate: string;
  vehicleClass: string;
  serialNumber: string;
  fuelType: string;
  noOfKms: string;
  issueDate: string;
  center: string;
  testFee: number;
  overallStatus: string;
  expiryDate: string;
}

interface InsuranceData {
  numberPlate: string;
  insurerCompanyName: string;
  policyNumber: string;
  policyHolderName: string;
  policyHolderAddress: string;
  coverageStartDate: string;
  coverageEndDate: string;
  coverageType: string;
  issuedDate: string;
}

interface RevenueLicenceData {
  numberPlate: string;
  revenueLicenceNumber: string;
  annualFee: number;
  arrears: number;
  fines: number;
  totalAmount: number;
  licenceStartDate: string;
  licenceEndDate: string;
  issuedDate: string;
}

const AddVehicleForm: React.FC = () => {
  const [step, setStep] = useState(1);
  const [vehicleData, setVehicleData] = useState({
    numberPlate: '',
    vehicleTypeId: { id: 0, type: '' } as VehicleType,
    brand: '',
    model: '',
    brandId: 0,
    modelId: 0,
    vehicleClassId: 0,
    condition: '',
    conditionId: 0,
    gearType: '',
    gearTypeId: 0,
    fuelType: '',
    fuelTypeId: { id: 0, fuelTypeName: '' } as FuelType,
    status: '',
    statusId: 0,
    featureIds: [] as number[],
    engineCapacity: null as number | null,
    batteryCapacity: null as number | null,
    ownerName: null as string | null,
    ownerAddress: null as string | null,
    noOfPassengers: 0,
    manufacturingYear: 0,
    chassisNo: null as string | null,
    engineNo: null as string | null,
    grossWeight: null as number | null,
    value: 0,
    noOfKms: 0,
    inStationEconomy: null as number | null,
    outStationEconomy: null as number | null,
    rentPerKm: 0,
    tyreSize: null as string | null,
    frontTyreSize: null as string | null,
    rearTyreSize: null as string | null,
    color: null as string | null,
    generalServiceStandardKms: 0,
    lastGeneralServiceKms: 0,
    lastGeneralServiceDate: null as string | null,
    advancedServiceStandardKms: 0,
    lastAdvancedServiceKms: 0,
    lastAdvancedServiceDate: null as string | null,
    standardKmsForTyreChange: 0,
    lastTyreChangeKms: 0,
    lastTyreChangeDate: null as string | null,
  });
  const [emissionTestData, setEmissionTestData] = useState<EmissionTestData>({
    numberPlate: '',
    vehicleClass: '',
    serialNumber: '',
    fuelType: '',
    noOfKms: '',
    issueDate: '',
    center: '',
    testFee: 0,
    overallStatus: '',
    expiryDate: '',
  });
  const [insuranceData, setInsuranceData] = useState<InsuranceData>({
    numberPlate: '',
    insurerCompanyName: '',
    policyNumber: '',
    policyHolderName: '',
    policyHolderAddress: '',
    coverageStartDate: '',
    coverageEndDate: '',
    coverageType: '',
    issuedDate: '',
  });
  const [revenueLicenceData, setRevenueLicenceData] = useState<RevenueLicenceData>({
    numberPlate: '',
    revenueLicenceNumber: '',
    annualFee: 0,
    arrears: 0,
    fines: 0,
    totalAmount: 0,
    licenceStartDate: '',
    licenceEndDate: '',
    issuedDate: '',
  });
  const [vehicleImages, setVehicleImages] = useState<File[]>([]);
  const [registrationBookImages, setRegistrationBookImages] = useState<File[]>([]);
  const [emissionTestImages, setEmissionTestImages] = useState<File[]>([]);
  const [insuranceImages, setInsuranceImages] = useState<File[]>([]);
  const [revenueLicenceImages, setRevenueLicenceImages] = useState<File[]>([]);
  const [vehicleTypes, setVehicleTypes] = useState<VehicleType[]>([]);
  const [features, setFeatures] = useState<VehicleFeature[]>([]);
  const [brandData, setBrandData] = useState<BrandData | null>(null);
  const [config, setConfig] = useState<Config>({
    conditions: [],
    states: [],
    gearTypes: [],
    fuelTypes: [],
    vehicleClasses: [],
    emissionOverallStatus: [],
    revenueLicenceStatus: [],
    features: {},
    brandModels: {},
  });
  const [formErrors, setFormErrors] = useState<FormErrors>({});
  const [isLoading, setIsLoading] = useState({
    vehicleTypes: false,
    features: false,
    config: false,
    brands: false,
    submit: false,
  });
  const isElectric = (vehicleData.fuelType || '') === 'Electric';

  // Format date as YYYY-MM-DDTHH:mm:ss+05:30
  const formatDateTime = (date: string | null): string | null => {
    if (!date) return null;
    return `${date}T00:00:00+05:30`;
  };

  // Fetch all initial data concurrently
  useEffect(() => {
    const fetchData = async () => {
      setIsLoading((prev) => ({
        ...prev,
        vehicleTypes: true,
        features: true,
        config: true,
        brands: true,
      }));
      try {
        const [vehicleTypesRes, featuresRes, configRes, brandsRes] = await Promise.all([
          apiDefinitions.getAllVehicleTypes(),
          apiDefinitions.getAllVehicleFeatures(),
          apiDefinitions.loadVehicleConfig(),
          apiDefinitions.getVehicleBrandModels('1', {}),
        ]);

        if (vehicleTypesRes.data.status === 'S1000' && Array.isArray(vehicleTypesRes.data.data)) {
          setVehicleTypes(vehicleTypesRes.data.data);
        } else {
          showError(vehicleTypesRes.data.statusDescription || 'Failed to fetch vehicle types');
        }

        if (featuresRes.data.status === 'S1000' && Array.isArray(featuresRes.data.data)) {
          setFeatures(featuresRes.data.data.map((f: any) => ({ id: f.id, featureName: f.featureName })));
        } else {
          showError(featuresRes.data.statusDescription || 'Failed to load features');
        }

        if (configRes.data.status === 'S1000' && configRes.data.data) {
          setConfig({
            ...configRes.data.data,
            emissionOverallStatus: configRes.data.data.emissionOverallStatus || ['Pass', 'Fail'],
            revenueLicenceStatus: configRes.data.data.revenueLicenceStatus || ['Valid', 'Expired'],
          });
        } else {
          showError(configRes.data.statusDescription || 'Failed to load configuration');
        }

        if (brandsRes.data.status === 'S1000' && brandsRes.data.data?.vehicleBrands) {
          setBrandData(brandsRes.data);
        } else {
          showError(brandsRes.data.statusDescription || 'Failed to load brands');
        }
      } catch (error: any) {
        showError(error.response?.data?.statusDescription || 'An error occurred fetching data');
      } finally {
        setIsLoading((prev) => ({
          ...prev,
          vehicleTypes: false,
          features: false,
          config: false,
          brands: false,
        }));
      }
    };

    fetchData();
  }, []);

  // Sync brandId and modelId when brand or model changes
  useEffect(() => {
    if (vehicleData.brand && brandData?.data?.vehicleBrands) {
      const selectedBrand = brandData.data.vehicleBrands.find((b) => b.name === vehicleData.brand);
      setVehicleData((prev) => ({ ...prev, brandId: selectedBrand?.id || 0 }));
    }
    if (vehicleData.model && vehicleData.brandId && brandData?.data?.vehicleBrands) {
      const selectedBrand = brandData.data.vehicleBrands.find((b) => b.id === vehicleData.brandId);
      const selectedModel = selectedBrand?.models?.find((m) => m.name === vehicleData.model);
      setVehicleData((prev) => ({ ...prev, modelId: selectedModel?.id || 0 }));
    }
  }, [vehicleData.brand, vehicleData.model, brandData]);

  // Handle file changes with validation (APPEND, not replace)
const handleFileChange = (
  e: React.ChangeEvent<HTMLInputElement>,
  setFiles: React.Dispatch<React.SetStateAction<File[]>>,
  maxSizeMB: number = 5,
  allowedTypes: string[] = ['image/jpeg', 'image/png', 'image/jpg']
) => {
  const input = e.target;
  if (!input.files) return;

  const incoming = Array.from(input.files);

  const validated = incoming.filter((file) => {
    const isValidType = allowedTypes.includes(file.type);
    const isValidSize = file.size <= maxSizeMB * 1024 * 1024;
    if (!isValidType) showError(`Invalid file type: ${file.name}. Allowed: ${allowedTypes.join(', ')}`);
    if (!isValidSize) showError(`File too large: ${file.name}. Max size: ${maxSizeMB}MB`);
    return isValidType && isValidSize;
  });

  // Append + de-dupe by name+size (simple heuristic)
  setFiles((prev) => {
    const map = new Map<string, File>();
    [...prev, ...validated].forEach((f) => map.set(`${f.name}-${f.size}`, f));
    return Array.from(map.values());
  });

  // Reset input value so selecting the same file list again still triggers onChange
  input.value = '';
};


  // Validate Step 1
 // Validate Step 1
const validateStep1 = () => {
  const errors: FormErrors = {};
  const isBike =
    vehicleData.vehicleTypeId.type === 'Motorcycle' ||
    vehicleData.vehicleTypeId.type === 'Bike';
  const isElectric = (vehicleData.fuelType || '').toLowerCase() === 'electric';
  const tyreSizeRegex = /^[0-9]{2,3}\/[0-9]{1,2}-[0-9]{1,2}$/;

  if (!vehicleData.numberPlate) errors.numberPlate = 'Number plate is required';
  if (!vehicleData.vehicleTypeId.id) errors.vehicleTypeId = 'Vehicle type is required';
  if (!vehicleData.brandId) errors.brand = 'Brand is required';
  if (!vehicleData.modelId) errors.model = 'Model is required';
  if (!vehicleData.vehicleClassId) errors.vehicleClassId = 'Vehicle class is required';
  if (!vehicleData.conditionId) errors.conditionId = 'Condition is required';
  if (!vehicleData.gearTypeId) errors.gearTypeId = 'Gear type is required';
  if (!vehicleData.fuelTypeId.id) errors.fuelTypeId = 'Fuel type is required';

  // Engine/chassis
  if (!isElectric && (!vehicleData.engineNo || vehicleData.engineNo.trim() === '')) {
    errors.engineNo = 'Engine number is required for non-electric vehicles';
  }
  if (!vehicleData.chassisNo || vehicleData.chassisNo.trim() === '') {
    errors.chassisNo = 'Chassis number is required';
  }

  // Basics
  if (!vehicleData.color || vehicleData.color.trim() === '') {
    errors.color = 'Color is required';
  }
  if (vehicleData.noOfPassengers <= 0) {
    errors.noOfPassengers = 'Number of passengers must be greater than 0';
  }
  const currentYear = new Date().getFullYear();
  if (
    !vehicleData.manufacturingYear ||
    vehicleData.manufacturingYear < 1900 ||
    vehicleData.manufacturingYear > currentYear
  ) {
    errors.manufacturingYear = 'Valid manufacturing year is required';
  }
  if (vehicleData.value <= 0) errors.value = 'Vehicle value must be greater than 0';
  if (vehicleData.noOfKms < 0) errors.noOfKms = 'Number of kilometers must be at least 0';

  // Powertrain
  if (!isElectric && vehicleData.engineCapacity == null) {
    errors.engineCapacity = 'Engine capacity must be provided for non-electric vehicles';
  }
  if (isElectric && (vehicleData.batteryCapacity == null || vehicleData.batteryCapacity <= 0)) {
    errors.batteryCapacity = 'Battery capacity must be greater than 0 for electric vehicles';
  }
  if (isElectric && vehicleData.inStationEconomy != null) {
    errors.inStationEconomy = 'In-station economy must be null for electric vehicles';
  }
  if (isElectric && vehicleData.outStationEconomy != null) {
    errors.outStationEconomy = 'Out-station economy must be null for electric vehicles';
  }
  if (!isElectric && (vehicleData.inStationEconomy == null || vehicleData.inStationEconomy <= 0)) {
    errors.inStationEconomy = 'In-station economy must be greater than 0 for non-electric vehicles';
  }
  if (!isElectric && (vehicleData.outStationEconomy == null || vehicleData.outStationEconomy <= 0)) {
    errors.outStationEconomy = 'Out-station economy must be greater than 0 for non-electric vehicles';
  }

  // ⚠️ Removed: generalServiceStandardKms required check (field was removed from UI)
  // if (vehicleData.generalServiceStandardKms <= 0) {
  //   errors.generalServiceStandardKms = 'General service standard must be greater than 0';
  // }

  // Keep Advanced Service Standard & Tyre Change standard as you still show them
  if (vehicleData.advancedServiceStandardKms <= 0) {
    errors.advancedServiceStandardKms = 'Advanced service standard must be greater than 0';
  }
  if (vehicleData.standardKmsForTyreChange <= 0) {
    errors.standardKmsForTyreChange = 'Standard kms for tyre change must be greater than 0';
  }

  // KMs: require + bounds
  // KMs: require + bounds (numbers only)
const lgs = Number(vehicleData.lastGeneralServiceKms);
if (!Number.isFinite(lgs)) {
  errors.lastGeneralServiceKms = 'Last general service kms is required';
} else if (lgs < 0) {
  errors.lastGeneralServiceKms = 'Last general service kms must be at least 0';
} else if (lgs > vehicleData.noOfKms) {
  errors.lastGeneralServiceKms = 'Last general service kms cannot exceed total kms';
}

const las = Number(vehicleData.lastAdvancedServiceKms);
if (Number.isFinite(las)) {
  if (las < 0) errors.lastAdvancedServiceKms = 'Last advanced service kms must be at least 0';
  else if (las > vehicleData.noOfKms) errors.lastAdvancedServiceKms = 'Last advanced service kms cannot exceed total kms';
}

const ltc = Number(vehicleData.lastTyreChangeKms);
if (Number.isFinite(ltc)) {
  if (ltc < 0) errors.lastTyreChangeKms = 'Last tyre change kms must be at least 0';
  else if (ltc > vehicleData.noOfKms) errors.lastTyreChangeKms = 'Last tyre change kms cannot exceed total kms';
}


  // Tyre sizes
  if (!isBike && (!vehicleData.tyreSize || vehicleData.tyreSize.trim() === '')) {
    errors.tyreSize = 'Tyre size is required for non-motorcycle vehicles';
  }
  if (!isBike && vehicleData.tyreSize && !tyreSizeRegex.test(vehicleData.tyreSize)) {
    errors.tyreSize = 'Invalid tyre size format (e.g., 185-65/15)';
  }
  if (isBike) {
    if (!vehicleData.frontTyreSize || vehicleData.frontTyreSize.trim() === '') {
      errors.frontTyreSize = 'Front tyre size is required';
    }
    if (!vehicleData.rearTyreSize || vehicleData.rearTyreSize.trim() === '') {
      errors.rearTyreSize = 'Rear tyre size is required';
    }
    if (vehicleData.frontTyreSize && !tyreSizeRegex.test(vehicleData.frontTyreSize)) {
      errors.frontTyreSize = 'Invalid front tyre size format (e.g., 250-11/45)';
    }
    if (vehicleData.rearTyreSize && !tyreSizeRegex.test(vehicleData.rearTyreSize)) {
      errors.rearTyreSize = 'Invalid rear tyre size format (e.g., 250-11/45)';
    }
  }

  setFormErrors(errors);
  return Object.keys(errors).length === 0;
};


  // Validate Step 2
  const validateStep2 = () => {
    const errors: FormErrors = {};
    if (vehicleImages.length === 0) errors.vehicleImages = 'At least one vehicle image is required';
    if (registrationBookImages.length === 0) errors.registrationBookImages = 'At least one registration book image is required';
    if (vehicleImages.length > 5) errors.vehicleImages = 'Maximum 5 vehicle images allowed';
    if (registrationBookImages.length > 5) errors.registrationBookImages = 'Maximum 5 registration book images allowed';
    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  // Validate Step 3
  const validateStep3 = () => {
  const errors: FormErrors = {};
  const todayStr = new Date().toISOString().split('T')[0];

  if (!emissionTestData.vehicleClass) errors.vehicleClass = 'Vehicle class is required';
  if (!emissionTestData.serialNumber) errors.serialNumber = 'Serial number is required';

  // Issue Date: required + cannot be in the future
  if (!emissionTestData.issueDate) {
    errors.issueDate = 'Issue date is required';
  } else if (emissionTestData.issueDate > todayStr) {
    errors.issueDate = 'Issue date cannot be in the future';
  }

  if (!emissionTestData.expiryDate) {
    errors.expiryDate = 'Expiry date is required';
  } else if (emissionTestData.issueDate && emissionTestData.expiryDate < emissionTestData.issueDate) {
    // ✅ Optional: logical order check
    errors.expiryDate = 'Expiry date cannot be before issue date';
  }

  if (!emissionTestData.overallStatus) errors.overallStatus = 'Overall status is required';
  if (!emissionTestData.center) errors.center = 'Test center is required';
  if (emissionTestData.testFee < 0) errors.testFee = 'Test fee must be at least 0';
  if (emissionTestImages.length > 5) errors.emissionTestImages = 'Maximum 5 emission test images allowed';

  setFormErrors(errors);
  return Object.keys(errors).length === 0;
};


  // Validate Step 4
  const validateStep4 = () => {
    const errors: FormErrors = {};
    if (!insuranceData.insurerCompanyName) errors.insurerCompanyName = 'Insurer company name is required';
    if (!insuranceData.policyNumber) errors.policyNumber = 'Policy number is required';
    if (!insuranceData.coverageStartDate) errors.coverageStartDate = 'Coverage start date is required';
    if (!insuranceData.coverageEndDate) errors.coverageEndDate = 'Coverage end date is required';
    if (insuranceImages.length > 5) errors.insuranceImages = 'Maximum 5 insurance images allowed';
    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  // Validate Step 5
  const validateStep5 = () => {
    const errors: FormErrors = {};
    if (!revenueLicenceData.revenueLicenceNumber) errors.revenueLicenceNumber = 'Revenue licence number is required';
    if (revenueLicenceData.annualFee < 0) errors.annualFee = 'Annual fee must be at least 0';
    if (revenueLicenceData.arrears < 0) errors.arrears = 'Arrears must be at least 0';
    if (revenueLicenceData.fines < 0) errors.fines = 'Fines must be at least 0';
    if (!revenueLicenceData.licenceStartDate) errors.licenceStartDate = 'Licence start date is required';
    if (!revenueLicenceData.licenceEndDate) errors.licenceEndDate = 'Licence end date is required';
    if (revenueLicenceImages.length > 5) errors.revenueLicenceImages = 'Maximum 5 revenue licence images allowed';
    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  // Validate all steps before submission
  const validateAllSteps = () => {
  let valid = true;

  // Step 1
  if (!validateStep1()) valid = false;

  // Step 2
  if (!validateStep2()) valid = false;

  // Step 3: SKIP VALIDATION FOR ELECTRIC
  if (!isElectric) {
    if (!validateStep3()) valid = false;
  }

  // Step 4
  if (!validateStep4()) valid = false;

  // Step 5
  if (!validateStep5()) valid = false;

  return valid;
};

  // Handle navigation between steps
  const handleNext = () => {
  if (step === 1 && validateStep1()) {
    // Sync number plate
    setEmissionTestData({ ...emissionTestData, numberPlate: vehicleData.numberPlate });
    setInsuranceData({ ...insuranceData, numberPlate: vehicleData.numberPlate });
    setRevenueLicenceData({ ...revenueLicenceData, numberPlate: vehicleData.numberPlate });

    // Skip Step 3 if Electric
    const isElectric = (vehicleData.fuelType || '')=== 'Electric';
    if (isElectric) {
      setStep(2); // Skip to Insurance
    } else {
      setStep(2);
    }
  } else if (step === 2 && validateStep2()) {
    const isElectric = (vehicleData.fuelType || '')=== 'Electric';
    if (isElectric) {
      setStep(4);
    } else {
      setStep(3);
    }
  } else if (step === 3 && validateStep3()) {
    setStep(4);
  } else if (step === 4 && validateStep4()) {
    setStep(5);
  }
};

  // Handle form submission
  const handleSubmit = async () => {
    if (!validateAllSteps()) {
      showError('Please correct errors in all steps before submitting');
      return;
    }

    const isConfirmed = await showConfirmSubmission(revenueLicenceData.totalAmount);
    if (!isConfirmed) return;

    setIsLoading((prev) => ({ ...prev, submit: true }));

    try {
      // Step 1: Add Vehicle
      const isElectric = vehicleData.fuelType.toLowerCase() === 'electric';
      const vehiclePayload = {
        numberPlate: vehicleData.numberPlate.toUpperCase(),
        vehicleTypeId: {
          id: vehicleData.vehicleTypeId.id,
          type: vehicleData.vehicleTypeId.type,
        },
        brandId: vehicleData.brandId,
        modelId: vehicleData.modelId,
        vehicleClassId: vehicleData.vehicleClassId,
        conditionId: vehicleData.conditionId,
        gearTypeId: vehicleData.gearTypeId,
        fuelTypeId: {
          id: vehicleData.fuelTypeId.id,
          fuelTypeName: vehicleData.fuelType,
        },
        statusId: vehicleData.statusId,
        featureIds: vehicleData.featureIds,
        engineCapacity: isElectric ? null : vehicleData.engineCapacity || null,
        batteryCapacity: isElectric ? vehicleData.batteryCapacity || null : null,
        ownerName: vehicleData.ownerName || null,
        ownerAddress: vehicleData.ownerAddress || null,
        noOfPassengers: vehicleData.noOfPassengers,
        manufacturingYear: vehicleData.manufacturingYear,
        chassisNo: vehicleData.chassisNo || null,
        engineNo: isElectric ? null : vehicleData.engineNo || null,
        grossWeight: vehicleData.grossWeight || null,
        value: vehicleData.value,
        noOfKms: vehicleData.noOfKms,
        inStationEconomy: isElectric ? null : vehicleData.inStationEconomy || null,
        outStationEconomy: isElectric ? null : vehicleData.outStationEconomy || null,
        rentPerKm: vehicleData.rentPerKm || null,
        tyreSize: !['Motorcycle', 'Bike'].includes(vehicleData.vehicleTypeId.type) ? vehicleData.tyreSize || null : null,
        frontTyreSize: ['Motorcycle', 'Bike'].includes(vehicleData.vehicleTypeId.type) ? vehicleData.frontTyreSize || null : null,
        rearTyreSize: ['Motorcycle', 'Bike'].includes(vehicleData.vehicleTypeId.type) ? vehicleData.rearTyreSize || null : null,
        color: vehicleData.color || null,
        generalServiceStandardKms: vehicleData.generalServiceStandardKms,
        lastGeneralServiceKms: vehicleData.lastGeneralServiceKms,
        lastGeneralServiceDate: formatDateTime(vehicleData.lastGeneralServiceDate),
        advancedServiceStandardKms: vehicleData.advancedServiceStandardKms,
        lastAdvancedServiceKms: vehicleData.lastAdvancedServiceKms,
        lastAdvancedServiceDate: formatDateTime(vehicleData.lastAdvancedServiceDate),
        standardKmsForTyreChange: vehicleData.standardKmsForTyreChange,
        lastTyreChangeKms: vehicleData.lastTyreChangeKms,
        lastTyreChangeDate: formatDateTime(vehicleData.lastTyreChangeDate),
      };

      const vehicleRes = await apiDefinitions.addVehicle(vehiclePayload);
      if (vehicleRes.data.status !== 'S1000') {
        showError(vehicleRes.data.statusDescription || 'Failed to add vehicle');
        return;
      }

      // Step 2: Upload Images
      const vehicleFormData = new FormData();
      vehicleImages.forEach((file) => vehicleFormData.append('images', file));
      const regBookFormData = new FormData();
      registrationBookImages.forEach((file) => regBookFormData.append('images', file));

      const [vehicleImageRes, regBookImageRes] = await Promise.all([
        apiDefinitions.uploadVehicleImages(vehicleData.numberPlate, vehicleFormData),
        apiDefinitions.uploadRegistrationBookImages(vehicleData.numberPlate, regBookFormData),
      ]);

      if (vehicleImageRes.data.status !== 'S1000' || regBookImageRes.data.status !== 'S1000') {
        showError(
          vehicleImageRes.data.statusDescription ||
            regBookImageRes.data.statusDescription ||
            'Failed to upload images'
        );
        return;
      }

      // Step 3: Add Emission Test
      if (!isElectric) {
      const emissionRequest = {
        numberPlate: emissionTestData.numberPlate,
        serialNumber: emissionTestData.serialNumber,
        issueDate: formatDateTime(emissionTestData.issueDate),
        center: emissionTestData.center,
        testFee: emissionTestData.testFee || 0,
        overallStatus: emissionTestData.overallStatus,
        expiryDate: formatDateTime(emissionTestData.expiryDate),
      };
      

      const emissionFormData = new FormData();
      emissionTestImages.forEach((file) => emissionFormData.append('images', file));

      const emissionRes = await apiDefinitions.addEmissionTest(emissionRequest, emissionFormData);
      if (emissionRes.data.status !== 'S1000') {
        showError(emissionRes.data.statusDescription || 'Failed to add emission test');
        return;
      }
    }

      // Step 4: Add Insurance
      const insuranceRequest = {
        numberPlate: insuranceData.numberPlate,
        insurerCompanyName: insuranceData.insurerCompanyName,
        policyNumber: insuranceData.policyNumber,
        policyHolderName: insuranceData.policyHolderName || null,
        policyHolderAddress: insuranceData.policyHolderAddress || null,
        coverageStartDate: formatDateTime(insuranceData.coverageStartDate),
        coverageEndDate: formatDateTime(insuranceData.coverageEndDate),
        coverageType: insuranceData.coverageType,
        issuedDate: formatDateTime(insuranceData.issuedDate),
      };

      const insuranceFormData = new FormData();
      insuranceImages.forEach((file) => insuranceFormData.append('images', file));

      const insuranceRes = await apiDefinitions.addInsurance(insuranceRequest, insuranceFormData);
      if (insuranceRes.data.status !== 'S1000') {
        showError(insuranceRes.data.statusDescription || 'Failed to add insurance');
        return;
      }

      // Step 5: Add Revenue Licence
      // Step 5: Add Revenue Licence
const revenueLicenceRequest = {
  numberPlate: revenueLicenceData.numberPlate,
  revenueLicenceNumber: revenueLicenceData.revenueLicenceNumber,
  annualFee: revenueLicenceData.annualFee ?? 0,
  arrears: revenueLicenceData.arrears ?? 0,
  fines: revenueLicenceData.fines ?? 0,
  totalAmount: revenueLicenceData.totalAmount ?? 0,
  licenceStartDate: formatDateTime(revenueLicenceData.licenceStartDate),
  licenceEndDate: formatDateTime(revenueLicenceData.licenceEndDate),
  issuedDate: revenueLicenceData.issuedDate ? formatDateTime(revenueLicenceData.issuedDate) : null,
};

const revenueLicenceFormData = new FormData();
revenueLicenceImages.forEach((file) => revenueLicenceFormData.append('images', file));

const revenueLicenceRes = await apiDefinitions.addRevenueLicence(revenueLicenceRequest, revenueLicenceFormData);
if (revenueLicenceRes.data.status !== 'S1000') {
  showError(revenueLicenceRes.data.statusDescription || 'Failed to add revenue licence');
  return;
}

      // All APIs succeeded
      showSuccessAlert("Vehicle added successfully");
      setStep(1);
      resetForm();
    } catch (error: any) {
      const errorDetails = error.response?.data?.errors
        ? Object.entries(error.response.data.errors)
            .map(([field, message]) => `${field}: ${message}`)
            .join('; ')
        : error.response?.data?.statusDescription || error.response?.data?.message || 'An error occurred during submission';
      showError(errorDetails);
    } finally {
      setIsLoading((prev) => ({ ...prev, submit: false }));
    }
  };

  // Reset form function
  const resetForm = () => {
    setVehicleData({
      numberPlate: '',
      vehicleTypeId: { id: 0, type: '' },
      brand: '',
      model: '',
      brandId: 0,
      modelId: 0,
      vehicleClassId: 0,
      condition: '',
      conditionId: 0,
      gearType: '',
      gearTypeId: 0,
      fuelType: '',
      fuelTypeId: { id: 0, fuelTypeName: '' },
      status: '',
      statusId: 0,
      featureIds: [],
      engineCapacity: null,
      batteryCapacity: null,
      ownerName: null,
      ownerAddress: null,
      noOfPassengers: 0,
      manufacturingYear: 0,
      chassisNo: null,
      engineNo: null,
      grossWeight: null,
      value: 0,
      noOfKms: 0,
      inStationEconomy: null,
      outStationEconomy: null,
      rentPerKm: 0,
      tyreSize: null,
      frontTyreSize: null,
      rearTyreSize: null,
      color: null,
      generalServiceStandardKms: 0,
      lastGeneralServiceKms: 0,
      lastGeneralServiceDate: null,
      advancedServiceStandardKms: 0,
      lastAdvancedServiceKms: 0,
      lastAdvancedServiceDate: null,
      standardKmsForTyreChange: 0,
      lastTyreChangeKms: 0,
      lastTyreChangeDate: null,
    });
    setVehicleImages([]);
    setRegistrationBookImages([]);
    setEmissionTestImages([]);
    setInsuranceImages([]);
    setRevenueLicenceImages([]);
    setEmissionTestData({
      numberPlate: '',
      vehicleClass: '',
      serialNumber: '',
      fuelType: '',
      noOfKms: '',
      issueDate: '',
      center: '',
      testFee: 0,
      overallStatus: '',
      expiryDate: '',
    });
    setInsuranceData({
      numberPlate: '',
      insurerCompanyName: '',
      policyNumber: '',
      policyHolderName: '',
      policyHolderAddress: '',
      coverageStartDate: '',
      coverageEndDate: '',
      coverageType: '',
      issuedDate: '',
    });
    setRevenueLicenceData({
      numberPlate: '',
      revenueLicenceNumber: '',
      annualFee: 0,
      arrears: 0,
      fines: 0,
      totalAmount: 0,
      licenceStartDate: '',
      licenceEndDate: '',
      issuedDate: '',
    });
    setFormErrors({});
  };

  // Handle previous step
  const handleBack = () => {
  if (step > 1) {
    const isElectric = (vehicleData.fuelType || '')=== 'Electric';
    if (step === 4 && isElectric) {
      setStep(2); // From Insurance → back to Images
    } else if (step === 3) {
      setStep(2);
    } else {
      setStep(step - 1);
    }
    setFormErrors({});
  }
};

  return (
    <>
      <div className="card">
        <div className="card-body pb-0">
          <div className="d-flex align-items-center justify-content-between mb-3">
            <h4 className="fs-18">Add Vehicle</h4>
          </div>
          <ul className="nav nav-tabs">
  <li className="nav-item">
    <span className={`nav-link ${step === 1 ? 'active' : ''}`}>1. Vehicle Details</span>
  </li>
  <li className="nav-item">
    <span className={`nav-link ${step === 2 ? 'active' : ''}`}>2. Images</span>
  </li>
  <li className="nav-item">
    <span className={`nav-link ${step === 3 ? 'active' : ''} ${isElectric ? 'text-muted' : ''}`}>
      3. Emission Test
      {isElectric && <small className="ms-2 text-success">(Skipped - Electric)</small>}
    </span>
  </li>
  <li className="nav-item">
    <span className={`nav-link ${step === 4 ? 'active' : ''}`}>4. Insurance</span>
  </li>
  <li className="nav-item">
    <span className={`nav-link ${step === 5 ? 'active' : ''}`}>5. Revenue Licence</span>
  </li>
</ul>
          <div className="mt-4">
            {step === 1 && (
              <StepOne
                vehicleData={vehicleData}
                setVehicleData={setVehicleData}
                vehicleTypes={vehicleTypes}
                features={features}
                config={config}
                isLoading={isLoading}
                brandData={brandData}
              />
            )}
            {step === 2 && (
              <StepTwo
                vehicleImages={vehicleImages}
                setVehicleImages={setVehicleImages}
                registrationBookImages={registrationBookImages}
                setRegistrationBookImages={setRegistrationBookImages}
                formErrors={formErrors}
                handleFileChange={handleFileChange}
              />
            )}
            {step === 3 && (
                isElectric ? (
                  <div className="text-center py-5">
                    <div className="alert alert-success">
                      <strong>Emission Test Not Required</strong><br />
                      Electric vehicles are exempt from emission testing.
                    </div>
                    <button className="btn btn-primary" onClick={() => setStep(4)}>
                      Continue to Insurance
                    </button>
                  </div>
                ) : (
                  <StepThree
                    emissionTestData={emissionTestData}
                    setEmissionTestData={setEmissionTestData}
                    emissionTestImages={emissionTestImages}
                    setEmissionTestImages={setEmissionTestImages}
                    formErrors={formErrors}
                    config={config}
                    isLoading={isLoading}
                    handleFileChange={handleFileChange}
                  />
                )
              )}
            {step === 4 && (
              <StepFour
                insuranceData={insuranceData}
                setInsuranceData={setInsuranceData}
                insuranceImages={insuranceImages}
                setInsuranceImages={setInsuranceImages}
                formErrors={formErrors}
                config={config}
                isLoading={isLoading}
                handleFileChange={handleFileChange}
              />
            )}
            {step === 5 && (
              <StepFive
                revenueLicenceData={revenueLicenceData}
                setRevenueLicenceData={setRevenueLicenceData}
                revenueLicenceImages={revenueLicenceImages}
                setRevenueLicenceImages={setRevenueLicenceImages}
                formErrors={formErrors}
                config={config}
                isLoading={isLoading}
                handleFileChange={handleFileChange}
              />
            )}
          </div>
          <div className="d-flex gap-2 mt-3">
            {step > 1 && (
              <button
                type="button"
                className="btn btn-secondary"
                onClick={handleBack}
                style={{ padding: '8px 16px' }}
                disabled={isLoading.submit}
              >
                Back
              </button>
            )}
            <button
              type="button"
              className="btn btn-success"
              onClick={step === 5 ? handleSubmit : handleNext}
              style={{ padding: '8px 16px' }}
              disabled={isLoading.submit}
            >
              {isLoading.submit ? (
                <>
                  <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                  Loading...
                </>
              ) : step === 5 ? (
                'Submit'
              ) : (
                'Next'
              )}
            </button>
          </div>
        </div>
      </div>
    </>
  );
};

export default AddVehicleForm;